import type { ReactElement, ReactNode } from 'react';
import CompactCard, { COMPACT_CARD_GRID } from '../../components/cards/CompactCard';
import { DASHBOARD_INFO } from '../../navigation/catalog';
import { DEPLOYMENT_LIST_SLUG, dashLabel, dashboardListPath, dashboardPath, type DashTab } from '../../navigation/dashboards';
import { APPEALS_LIST } from '../../components/dashboard/lists/appeals';
import { APPROVALS_LIST } from '../../components/dashboard/lists/approvals';
import { ATTENDANCE_LIST } from '../../components/dashboard/lists/attendance';
import { EXITS_SLUG } from '../../components/dashboard/lists/attrition';
import { MOOD_LIST } from '../../components/dashboard/lists/mood';
import { NO_BUDDY_SLUG } from '../../components/dashboard/lists/onboarding';
import { RED_FLAG_LIST } from '../../components/dashboard/lists/redFlag';
import { YELLOW_FLAG_LIST } from '../../components/dashboard/lists/yellowFlag';
import { NOT_MARKED_SLUG } from '../../components/dashboard/lists/training';
import { fmtNumber, type KeptCount } from './homeNumbers';
import type { DashboardNumbers } from './useHomeNumbers';
import { OFF, valueOf, type Read } from './useRead';

interface DashboardCardsProps {
  /** The person's dashboards, already in card order. */
  tabs: readonly DashTab[];
  numbers: DashboardNumbers;
  /** Drawn under the cards, inside the same block: the dashboards the team built. */
  children?: ReactNode;
}

interface Headline {
  number: Read<number>;
  /**
   * One to three grey words beside the number on the card: "142" + "stores short". The
   * same for one and for several, on every card but Data Hygiene's (see headlineFor).
   */
  label: string;
  /** What the number counts, in full, for the hover: "142 stores short of people". */
  meaning: string;
  /** `action` (amber above 0) for a number that is work waiting; `plain` (dark) for one that only informs. */
  tone: 'action' | 'plain';
  /** When the number was read ("10:40 am"), for one the server keeps in the background. */
  asOf: string | null;
}

const counted = (number: Read<number>, label: string, meaning: string, tone: Headline['tone']): Headline =>
  ({ number, label, meaning, tone, asOf: null });

// A number the server keeps in the background is as old as the time in its hover. While
// it is not known the card has no number, like any other card whose number failed.
const kept = (read: Read<KeptCount>, label: string, meaning: string, tone: Headline['tone']): Headline => ({
  number: read.status === 'ready' ? { status: 'ready', value: read.value.count } : read,
  label,
  meaning,
  tone,
  asOf: read.status === 'ready' ? read.value.asOf : null,
});

/** A dashboard with no number of its own: nothing is asked for it, and nothing is shown as one. */
const NO_HEADLINE: Headline = { number: OFF, label: '', meaning: '', tone: 'plain', asOf: null };

// The one number a card carries, its few words, and whether it is work waiting (amber,
// as the owner's mockup of 22 Oct 2026 coloured them) or only a figure to read (dark).
// Only these twelve have one that can be asked cheaply and truthfully today; Score
// Movement, Hiring and EWS have none.
function headlineFor(tab: DashTab, numbers: DashboardNumbers): Headline {
  switch (tab) {
    case 'Deployment':
      // The dashboard's Undeployed Stores list.
      return kept(numbers.storesShort, 'stores short', 'stores short of people', 'action');
    case 'Onboarding':
      // The count on the "Needs attention" card, from the same request.
      return counted(numbers.noBuddy, 'waiting for a buddy', 'new joiners waiting for a buddy', 'action');
    case 'Uniform & Asset Management':
      return counted(numbers.uniformPastTarget, 'past 3-day target', 'past the 3-day target', 'action');
    case 'Data Hygiene': {
      // The count on the dashboard's Stores switch. The one card worded for one: on a
      // card about mistakes, "1 stores to fix" would read as one.
      const words = valueOf(numbers.storesToFix) === 1 ? 'store to fix' : 'stores to fix';
      return counted(numbers.storesToFix, words, words, 'action');
    }
    case 'Attendance':
      // "Had not", never "have not": someone may have checked in since the time it was read.
      return kept(numbers.absentToday, 'not checked in', 'had not checked in', 'plain');
    case 'Approvals':
      // The dashboard's Pending list: this month's requests. The "Needs attention" card
      // counts what waits on My Approvals instead, so the words say which month.
      return kept(numbers.requestsPending, 'pending this month', 'requests pending this month', 'plain');
    case 'Mood':
      return counted(numbers.lowMood, 'unhappy 2+ days', 'unhappy on 2+ days', 'plain');
    case 'Red Flag':
      // The count on the dashboard's "Red Flags Today" block.
      return counted(numbers.flaggedToday, 'flagged today', 'flagged today', 'plain');
    case 'Yellow Flag':
      return counted(numbers.yellowRaised, 'raised this month', 'raised this month', 'plain');
    case 'Appeals':
      // The dashboard's "Open red-flag" and "Open yellow-flag" tiles added together.
      return counted(numbers.appealsOpen, 'to decide', 'appeals waiting for a decision', 'action');
    case 'Training':
      // The dashboard's "Not marked" figure.
      return counted(numbers.trainingNotMarked, 'not marked', 'attendance not marked', 'action');
    case 'Attrition':
      // The "Exits" of this month's row on the dashboard: last working days still to
      // come this month included, as the dashboard counts them.
      return counted(numbers.attritionExits, 'exits this month', 'exits this month', 'plain');
    default:
      return NO_HEADLINE;
  }
}

// Where a card opens: the dashboard ON the list that holds the people, stores or flags
// its number counts, so whoever clicks "165 stores short of people" lands on those 165
// and what to do about them, not on the dashboard's front page. Each list opens with the
// month and Role filter the number was counted with (the dashboard's own defaults, which
// useDashboardNumbers asks with). Data Hygiene opens on its Stores side by itself. Uniform
// is left out: its number is worked out inside 1Office, and the rows ChannelPlex holds
// cannot list exactly the requests it counts, so it opens plainly, as do Hiring and Score
// Movement, which have no number.
const COUNTED_LIST: Partial<Record<DashTab, string>> = {
  Deployment: DEPLOYMENT_LIST_SLUG['Undeployed Stores'],
  Onboarding: NO_BUDDY_SLUG,
  Attrition: EXITS_SLUG,
  Attendance: ATTENDANCE_LIST.absentToday,
  Approvals: APPROVALS_LIST.pending,
  Mood: MOOD_LIST.lowMood,
  'Red Flag': RED_FLAG_LIST.flaggedToday,
  'Yellow Flag': YELLOW_FLAG_LIST.raised,
  // Red and yellow together: the open queue the number counts, as one list.
  Appeals: APPEALS_LIST.open,
  Training: NOT_MARKED_SLUG,
};

/** The address a dashboard's card opens. */
export function dashboardCardPath(tab: DashTab): string {
  const list = COUNTED_LIST[tab];
  return list ? dashboardListPath(tab, list) : dashboardPath(tab);
}

/** A card's hover title: the question its dashboard answers, the number in full, and when a kept number was read. */
function hoverFor(question: string, { number, meaning, asOf }: Headline): string {
  return [question, number.status === 'ready' ? `${fmtNumber(number.value)} ${meaning}` : null, asOf ? `As of ${asOf}` : null]
    .filter(Boolean)
    .join('\n');
}

function DashboardCard({ tab, numbers }: { tab: DashTab; numbers: DashboardNumbers }): ReactElement {
  const info = DASHBOARD_INFO[tab];
  const headline = headlineFor(tab, numbers);
  const { number, label, tone } = headline;
  return (
    <CompactCard
      to={dashboardCardPath(tab)}
      icon={info.icon}
      name={dashLabel(tab)}
      // No number (none wired, not asked, refused, failed, not known): never a zero. The
      // card says a grey "Open", and the question the dashboard answers is its hover.
      figures={number.status === 'ready' ? [{ value: number.value, label, tone }] : []}
      loading={number.status === 'loading'}
      hover={hoverFor(info.question, headline)}
    />
  );
}

/**
 * "Dashboards": one compact card per dashboard the person may open, in one grid, and the
 * whole card opens the dashboard (components/cards/CompactCard). A card is the
 * dashboard's icon, its name, and its one number with a few words; a dashboard with no
 * number says a grey "Open". The question the dashboard answers is the card's hover.
 *
 * It has no heading of its own: the home screen's tab strip names it.
 */
export default function DashboardCards({ tabs, numbers, children }: DashboardCardsProps): ReactElement | null {
  if (tabs.length === 0) return null;

  return (
    <section aria-label="Dashboards">
      <ul aria-label="Dashboards" className={COMPACT_CARD_GRID}>
        {tabs.map((tab) => (
          <li key={tab} className="min-w-0">
            <DashboardCard tab={tab} numbers={numbers} />
          </li>
        ))}
      </ul>

      {children && <div className="mt-3.5">{children}</div>}
    </section>
  );
}
